import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import DashboardShell from "@/components/DashboardShell";
import { LayoutDashboard, UserCheck, Users, CalendarDays, Star, Loader2, Check, X, Ban, Flag, MapPin, ShieldAlert, ShieldCheck, Scale, Receipt, Rocket } from "lucide-react";
import AdminTrustTab from "@/components/AdminTrustTab";
import AdminDbsTab from "@/components/AdminDbsTab";
import AdminPricingTab from "@/components/AdminPricingTab";
import AdminReconciliationTab from "@/components/AdminReconciliationTab";
import AdminReadinessTab from "@/components/AdminReadinessTab";
import AdminRecruitmentPanel from "@/components/AdminRecruitmentPanel";
import AdminUserDataReset from "@/components/AdminUserDataReset";
import { toast } from "sonner";
import { BarChart, Bar, PieChart, Pie, Cell, ResponsiveContainer, XAxis, YAxis, Tooltip } from "recharts";
import BookingStatusBadge from "@/components/BookingStatusBadge";

const COLORS = ["#112A22", "#B46B47", "#E5D9C5", "#2D5A43", "#7A827E", "#c98a63"];

export default function AdminDashboard() {
  const [active, setActive] = useState("overview");
  const [analytics, setAnalytics] = useState(null);
  const [cleaners, setCleaners] = useState([]);
  const [users, setUsers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [reports, setReports] = useState([]);
  const [demand, setDemand] = useState({ requests: [], top_areas: [] });

  const load = async () => {
    const [a, c, u, b, r, rep] = await Promise.all([
      api.get("/admin/analytics"), api.get("/admin/cleaners"), api.get("/admin/users"),
      api.get("/admin/bookings"), api.get("/admin/reviews"), api.get("/admin/reports"),
    ]);
    setAnalytics(a.data); setCleaners(c.data.cleaners); setUsers(u.data.users);
    setBookings(b.data.bookings); setReviews(r.data.reviews); setReports(rep.data.reports);
    api.get("/demand/admin").then((d) => setDemand(d.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const approve = async (cleaner_id, approve) => {
    try { await api.post("/admin/cleaners/approve", { cleaner_id, approve }); toast.success(approve ? "Cleaner activated" : "Cleaner restricted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const suspend = async (user_id, s) => {
    try { await api.post(`/admin/users/${user_id}/suspend?suspend=${s}`); toast.success(s ? "Suspended" : "Unsuspended"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const moderate = async (review_id, approve) => {    try { await api.post(`/admin/reviews/${review_id}/moderate?approve=${approve}`); toast.success("Done"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const resolveReport = async (report_id) => {
    try { await api.post(`/admin/reports/${report_id}/resolve`); toast.success("Report resolved"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const tabs = [
    { key: "overview", label: "Analytics", icon: LayoutDashboard },
    { key: "approvals", label: "Approvals", icon: UserCheck },
    { key: "users", label: "Users", icon: Users },
    { key: "bookings", label: "Bookings", icon: CalendarDays },
    { key: "reviews", label: "Reviews", icon: Star },
    { key: "reports", label: "Safety", icon: Flag },
    { key: "demand", label: "Demand", icon: MapPin },
    { key: "trust", label: "Trust & Safety", icon: ShieldAlert },
    { key: "dbs", label: "DBS", icon: ShieldCheck },
    { key: "pricing", label: "Pricing", icon: Scale },
    { key: "reconciliation", label: "Reconciliation", icon: Receipt },
    { key: "readiness", label: "Beta Readiness", icon: Rocket },
  ];

  const pending = cleaners.filter((c) => c.status === "pending");

  return (
    <DashboardShell title="Admin Dashboard" tabs={tabs} active={active} onTab={setActive}>
      {!analytics ? <Loader2 className="h-6 w-6 animate-spin text-accent" /> : (
        <>
          {active === "overview" && (
            <div className="space-y-8" data-testid="admin-analytics">
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Stat label="Platform revenue" value={`£${analytics.platform_revenue.toFixed(2)}`} />
                <Stat label="GMV" value={`£${analytics.gmv.toFixed(2)}`} />
                <Stat label="Total bookings" value={analytics.total_bookings} />
                <Stat label="Pending approvals" value={analytics.pending_approvals} />
                <Stat label="Total users" value={analytics.total_users} />
                <Stat label="Cleaners" value={analytics.total_cleaners} />
                <Stat label="Premium cleaners" value={analytics.premium_cleaners} />
              </div>
              <div className="grid lg:grid-cols-2 gap-6">
                <div className="bg-card rounded-2xl border border-primary/5 p-6">
                  <h3 className="font-display font-semibold mb-4">Bookings by status</h3>
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={Object.entries(analytics.bookings_by_status).map(([k, v]) => ({ name: k.replace(/_/g, " "), value: v }))}>
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} /><YAxis allowDecimals={false} /><Tooltip />
                      <Bar dataKey="value" fill="#B46B47" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="bg-card rounded-2xl border border-primary/5 p-6">
                  <h3 className="font-display font-semibold mb-4">Revenue by service</h3>
                  {analytics.revenue_by_service.length ? (
                    <ResponsiveContainer width="100%" height={240}>
                      <PieChart>
                        <Pie data={analytics.revenue_by_service} dataKey="value" nameKey="name" outerRadius={90} label>
                          {analytics.revenue_by_service.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : <p className="text-muted-foreground text-sm py-16 text-center">No revenue data yet.</p>}
                </div>
              </div>
              <AdminUserDataReset />   {/* hides itself unless the API confirms super admin */}
            </div>
          )}

          {active === "approvals" && (
            <div className="space-y-4" data-testid="admin-approvals">
              {pending.length === 0 ? <p className="text-muted-foreground py-10 text-center">No cleaners awaiting approval.</p> :
                pending.map((c) => (
                  <div key={c.cleaner_id} className="bg-card rounded-2xl border border-primary/5 p-5 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <img src={c.photo_url || "https://via.placeholder.com/64"} alt="" className="h-12 w-12 rounded-xl object-cover" />
                      <div><p className="font-medium">{c.name}</p><p className="text-sm text-muted-foreground">{c.town} · £{c.hourly_rate}/hr · {c.type}</p></div>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => approve(c.cleaner_id, true)} className="inline-flex items-center gap-1 px-4 py-2 rounded-full bg-green-600 text-white text-sm font-medium hover:bg-green-700 transition-colors" data-testid={`approve-${c.cleaner_id}`}><Check className="h-4 w-4" /> Activate</button>
                      <button onClick={() => approve(c.cleaner_id, false)} className="inline-flex items-center gap-1 px-4 py-2 rounded-full border border-border text-sm font-medium hover:bg-secondary transition-colors" data-testid={`reject-${c.cleaner_id}`}><X className="h-4 w-4" /> Restrict</button>
                    </div>
                  </div>
                ))}
            </div>
          )}

          {active === "users" && (
            <div className="bg-card rounded-2xl border border-primary/5 overflow-hidden" data-testid="admin-users">
              <table className="w-full text-sm">
                <thead className="bg-secondary/50 text-left"><tr><th className="px-4 py-3">Name</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Role</th><th className="px-4 py-3">Status</th><th className="px-4 py-3"></th></tr></thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.user_id} className="border-t border-border">
                      <td className="px-4 py-3">{u.name}</td><td className="px-4 py-3 text-muted-foreground">{u.email}</td>
                      <td className="px-4 py-3 capitalize">{u.role?.replace("_", " ")}</td>
                      <td className="px-4 py-3">{u.suspended ? <span className="text-destructive">Suspended</span> : "Active"}</td>
                      <td className="px-4 py-3 text-right">
                        {!["admin", "super_admin"].includes(u.role) && (
                          <button onClick={() => suspend(u.user_id, !u.suspended)} className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border border-border hover:bg-secondary transition-colors" data-testid={`suspend-${u.user_id}`}><Ban className="h-3 w-3" /> {u.suspended ? "Unsuspend" : "Suspend"}</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {active === "bookings" && (
            <div className="space-y-3" data-testid="admin-bookings">
              {bookings.map((b) => (
                <div key={b.booking_id} className="bg-card rounded-2xl border border-primary/5 p-4 flex items-center justify-between">
                  <div><p className="font-medium flex items-center gap-2">{b.service_name} <BookingStatusBadge status={b.status} /></p><p className="text-sm text-muted-foreground">{b.customer_name} → {b.cleaner_name} · {b.date}</p></div>
                  <span className="font-medium">£{b.price_total?.toFixed(2)}</span>
                </div>
              ))}
              {bookings.length === 0 && <p className="text-muted-foreground py-10 text-center">No bookings yet.</p>}
            </div>
          )}

          {active === "reviews" && (
            <div className="space-y-3" data-testid="admin-reviews">
              {reviews.map((r) => (
                <div key={r.review_id} className="bg-card rounded-2xl border border-primary/5 p-4">
                  <div className="flex justify-between">
                    <span className="font-medium">{r.customer_name} <span className="inline-flex items-center gap-1 ml-2"><Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" /> {r.rating}</span></span>
                    <span className={`text-xs ${r.status === "approved" ? "text-green-600" : "text-muted-foreground"}`}>{r.status}</span>
                  </div>
                  <p className="text-muted-foreground mt-1">{r.text}</p>
                  <div className="flex gap-2 mt-3">
                    <button onClick={() => moderate(r.review_id, true)} className="text-xs px-3 py-1.5 rounded-full bg-green-600 text-white" data-testid={`approve-review-${r.review_id}`}>Approve</button>
                    <button onClick={() => moderate(r.review_id, false)} className="text-xs px-3 py-1.5 rounded-full border border-border" data-testid={`hide-review-${r.review_id}`}>Hide</button>
                  </div>
                </div>
              ))}
              {reviews.length === 0 && <p className="text-muted-foreground py-10 text-center">No reviews yet.</p>}
            </div>
          )}

          {active === "reports" && (
            <div className="space-y-4" data-testid="admin-reports">
              {reports.length === 0 ? <p className="text-muted-foreground py-10 text-center">No reports submitted. All clear!</p> :
                reports.map((r, i) => (
                  <div key={r.report_id || i} className="bg-card rounded-2xl border border-primary/5 p-5" data-testid={`report-${i}`}>
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <p className="font-medium"><Flag className="h-4 w-4 inline text-destructive" /> {r.reporter_name} reported {r.reported_name}</p>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${r.status === "resolved" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{r.status}</span>
                        {r.status !== "resolved" && (
                          <button onClick={() => resolveReport(r.report_id)} className="text-xs px-3 py-1.5 rounded-full bg-primary text-primary-foreground" data-testid={`resolve-report-${r.report_id}`}>Mark resolved</button>
                        )}
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">Reason: {r.reason}</p>
                    {r.recent_messages?.length > 0 && (
                      <div className="mt-3 bg-secondary/40 rounded-xl p-3 space-y-1 max-h-48 overflow-y-auto">
                        {r.recent_messages.map((m) => (
                          <p key={m.message_id} className="text-xs"><span className="font-medium">{m.sender_name}:</span> {m.text}</p>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
            </div>
          )}

          {active === "trust" && <AdminTrustTab />}
          {active === "dbs" && <AdminDbsTab />}
          {active === "pricing" && <AdminPricingTab />}
          {active === "reconciliation" && <AdminReconciliationTab />}
          {active === "readiness" && <AdminReadinessTab />}


          {active === "demand" && (
            <div className="space-y-5" data-testid="admin-demand">              <div>
                <h3 className="font-display font-semibold">Demand capture</h3>
                <p className="text-sm text-muted-foreground mt-0.5">
                  Searches that found no available cleaners. "Notify me" requests are also emailed to the admin inbox.
                </p>
              </div>

              <AdminRecruitmentPanel />

              {demand.top_areas.length > 0 && (
                <div className="bg-card rounded-2xl border border-primary/5 p-5" data-testid="demand-top-areas">
                  <h4 className="text-sm font-semibold">Top areas requested</h4>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {demand.top_areas.map((a) => (
                      <span key={a.area} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-secondary text-sm">
                        <MapPin className="h-3.5 w-3.5 text-accent" /> {a.area} · <strong>{a.count}</strong>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {demand.requests.length === 0 ? (
                <p className="text-muted-foreground py-10 text-center">No unmet demand recorded yet.</p>
              ) : (
                <div className="space-y-3">
                  {demand.requests.map((d) => (
                    <div key={d.demand_id} className="bg-card rounded-2xl border border-primary/5 p-4" data-testid={`demand-${d.demand_id}`}>
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-medium text-sm">{d.postcode || "No postcode"} · {d.service || "any service"}</p>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${d.source === "form" ? "bg-accent/10 text-accent" : "bg-secondary text-muted-foreground"}`}>
                          {d.source === "form" ? "Notify me request" : "Search only"}
                        </span>
                        <span className="ml-auto text-xs text-muted-foreground">
                          {new Date(d.created_at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        {d.user_name || "Guest"}{d.user_email ? ` · ${d.user_email}` : ""} · {d.user_role || "not signed in"}
                        {d.date ? ` · wanted ${d.date}` : ""}{d.slot ? ` ${d.slot}` : ""} · via {d.context}
                      </p>
                      {d.notes && <p className="text-sm mt-1 italic">"{d.notes}"</p>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

        </>
      )}
    </DashboardShell>
  );
}

function Stat({ label, value }) {
  return <div className="bg-card rounded-2xl border border-primary/5 p-6"><p className="font-display text-2xl font-semibold">{value}</p><p className="text-sm text-muted-foreground mt-1">{label}</p></div>;
}
